<script>
import Vue from 'vue';
export default {
  inheritAttrs: true,
  inject: {
    form: {
      type: Object,
      default: () => ({}),
    },
  },
  props: {
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    tooltip: {
      type: String,
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    valid: {
      type: Boolean,
      default: true,
    },
    memory: {
      type: Boolean,
      default: true,
    },
    rules: {
      type: String,
      default: '',
    },
    secondValue: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      model: this.value,
      focused: false,
      pristine: true,
      errorCode: '',
      visible: false,
      invalidValues: {},
      behaviourCapture: {
        eventStart: null,
        eventEnd: null,
        pasted: false,
        firstValue: null,
        edited: false,
      },
    };
  },
  computed: {
    /**
     * Field is active if its focused or has value
     * Active field has different visualisation
     */
    active() {
      return Boolean(this.focused || this.model);
    },

    // Main validation source
    isValid() {
      return Boolean(
        // computed parent validation
        this.valid &&
          // has value if required
          (!this.required || this.model) &&
          // not invalidated without memory
          !this.errorStatus &&
          // not valid by rules
          !this.errorElement
      );
    },

    /** Field was touched or form was submitted */
    isDirty() {
      return !this.pristine || this.form.pristine === false;
    },

    /**
     * Show errors if
     * - Field is invalid
     * - Field is not pristine
     */
    showError() {
      return !this.isValid && this.isDirty;
    },

    /** Show success only if field was touched */
    showSuccess() {
      return this.isValid && this.isDirty;
    },

    /**
     * Show tooltip if
     * - Tooltip has text to display
     * - FIeld is focused
     */
    showTooltip() {
      return Boolean(this.tooltip && this.focused);
    },

    /** Detect if field is of password type to remove sensitive data handlers */
    isPassword() {
      return [this.name, this.type].includes('password');
    },

    /** User behaviour data payload */
    behaviour() {
      const { eventStart, eventEnd, firstValue, pasted, edited } = this.behaviourCapture;

      return {
        eventStart,
        eventEnd,
        firstValue,
        edited,
        pasted,
        eventDuration: eventEnd - eventStart,
        value: this.isPassword ? null : this.model,
      };
    },

    /** Key by which error is being saved */
    errorKey() {
      return this.model || '__empty__';
    },

    /** Error status code */
    errorStatus() {
      const key = this.errorKey;
      if (this.memory) {
        return this.invalidValues[key] || '';
      } else if (this.errorCode) {
        return this.errorCode;
      } else {
        return this.error;
      }
    },

    /** Context passed to validator */
    validatorContext() {
      return {
        value: this.model,
        secondValue: this.secondValue,
        errorStatus: this.errorStatus,
        valid: this.valid,
      };
    },

    /** Error payload from validator */
    errorElement() {
      const actualRule = this.rules
        .split(',')
        .map(el => el.trim())
        .filter(Boolean)
        .find(rule => {
          return this.$validator.apply(rule, this.validatorContext);
        });

      return actualRule ? this.$validator.getElement(actualRule) : false;
    },
  },
  watch: {
    // Watch value prop update and sync model
    value(val) {
      this.setValue(val);
    },

    /** Detect 2nd value change for behaviour */
    model() {
      if (this.behaviourCapture.eventEnd) this.behaviourCapture.edited = true;
    },
  },
  methods: {
    // Reset field element
    reset() {
      this.pristine = true;
      this.errorCode = '';
      this.invalidValues = {};
      this.behaviourCapture = {
        eventStart: null,
        eventEnd: null,
        pasted: false,
        firstValue: null,
        edited: false,
      };
    },

    // Focus on the field element
    focus() {
      this.$refs.element.focus();
    },

    // Blur field element
    blur() {
      this.$refs.element.blur();
    },

    // Remove pristine state and enable errors
    touch() {
      this.pristine = false;
    },

    /** Value setter */
    setValue(value) {
      this.model = value;
      this.$emit('input', this.model);
    },

    // On field focus
    onFocus() {
      // Set focused to true
      this.focused = true;
      // Disable manual error
      this.errorCode = '';
      // Emit focus event to parent
      this.$emit('focus');
      // Capture first focus
      if (this.behaviourCapture.eventStart === null) {
        this.behaviourCapture.eventStart = Date.now();
      }
    },

    // On blur event
    onBlur() {
      // Set focused to false
      this.focused = false;
      // Disable manual error
      this.errorCode = '';
      // Set pristine to false
      this.touch();
      // Emit blur event to parent
      this.$emit('blur');
      this.behaviourCapture.eventEnd = Date.now();
      if (this.behaviourCapture.firstValue === null && !this.isPassword) {
        this.behaviourCapture.firstValue = this.model;
      }
    },

    /** Invalidate current value
     *  1. With memory enabled (default) remember invalid values
     *  2. With memory disabled, keep error until focus / blur event
     */
    invalidate(errorCode) {
      const code = errorCode || 'validationError';
      this.pristine = false;

      if (this.memory) {
        Vue.set(this.invalidValues, this.errorKey, code);
      } else {
        this.errorCode = code;
      }
    },
  },
};
</script>
